import React, {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Form, FormRenderProps } from 'react-final-form';
import { useModal } from 'react-modal-hook';
import classNames from 'classnames';
import InteractModal from 'Components/Interact/InteractModal';
import UISpinner from 'Components/UIComponents/UISpinner';
import arrayMutators from 'final-form-arrays';
import { useIsMobile } from 'Hooks/Common';
import { useDocumentUpdate } from 'Hooks/Document';
import {
  Document,
  DocumentTypes,
  DocumentValues,
  FinalStepButtonTitle,
  Signer,
} from 'Interfaces/Document';
import { orderBy } from 'lodash-es';

import { LandingDocumentForm, LandingSetTitleForm } from './components';
import { StepType } from './LandingSignUpWrapper';

interface LandingSignDocumentProps {
  document?: Document;
  documentId?: string;
  setDocumentId: Dispatch<SetStateAction<string | undefined>>;
  setStepType: Dispatch<SetStateAction<StepType>>;
}

function getFinalStepButtonTitle(documentType: DocumentTypes) {
  switch (documentType) {
    case DocumentTypes.ME:
      return FinalStepButtonTitle.SIGN_DOCUMENT;
    case DocumentTypes.ME_AND_OTHER:
      return FinalStepButtonTitle.SIGN_AND_SEND;
    case DocumentTypes.OTHERS:
      return FinalStepButtonTitle.SEND;
  }
}

const LandingSignDocument = ({
  document,
  documentId,
  setDocumentId,
  setStepType,
}: LandingSignDocumentProps) => {
  const isMobile = useIsMobile();

  const initialValues = useMemo(() => {
    return {
      type: DocumentTypes.ME,
      signers: [
        {
          name: 'Me (Now)',
          email: 'example@example.com',
          order: -1,
          isPreparer: true,
        },
      ],
    } as DocumentValues;
  }, []);

  const [updateDocument, isUpdatingDocument] = useDocumentUpdate();

  const [currentStep, setCurrentStep] = useState(1);
  const [isDisableInteractTooltip, disableInteractTooltip] = useState<boolean>(false);

  const isUploadedFile = useMemo(
    () =>
      document &&
      document.parts.length &&
      document.parts.every(part => part.filesUploaded),
    [document],
  );

  const formattedInitialValues = useMemo(() => {
    const documentSigners = ((document?.signers.length || 0) >=
    (initialValues.signers?.length || 0)
      ? document?.signers
      : initialValues.signers) as Signer[];
    const recipients = initialValues?.recipients
      ? initialValues?.recipients
      : document?.recipients;

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { fields, ...restDocument } = document || {};

    return {
      ...initialValues,
      ...restDocument,
      signers: orderBy(documentSigners, 'order', 'asc'),
      isOrdered: document?.isOrdered || initialValues.isOrdered,
      type: initialValues.type,
      recipients,
      title: document?.title,
      message: document?.message,
    } as DocumentValues;
  }, [document, initialValues]);

  const [showInteractModal, closeInteractModal] = useModal(() => {
    return (
      <InteractModal
        onClose={closeInteractModal}
        documentId={documentId as string}
        handleSubmit={closeInteractModal}
        submitting={isUpdatingDocument}
        documentInitialValues={formattedInitialValues}
        wizardFormStep={currentStep}
        onChangeWizardFormStep={() => {
          setCurrentStep(prev => prev + 1);
        }}
        onChooseStep={() => {}}
        disableTooltip={disableInteractTooltip}
        isDisableTooltip={isDisableInteractTooltip}
        isChooseActionDisabled={true}
        isDisableCancelButton={true}
      />
    );
  }, [currentStep, documentId, isUpdatingDocument, document]);

  const onSubmit = useCallback(
    async (values: DocumentValues) => {
      if (document) {
        await updateDocument({
          values: {
            documentId: document.id,
            title: values.title,
          },
        });

        setCurrentStep(4);
        setStepType(StepType.SIGN_UP);
      }
    },
    [document, setStepType, updateDocument],
  );

  const handleOpenInteract = useCallback(() => {
    setCurrentStep(2);
    showInteractModal();
  }, [showInteractModal]);

  useEffect(() => {
    if (isUploadedFile) {
      handleOpenInteract();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isUploadedFile]);

  if (currentStep > 3) {
    return <UISpinner width={50} height={50} wrapperClassName="spinner--main__wrapper" />;
  }

  return (
    <Form
      initialValues={formattedInitialValues}
      onSubmit={onSubmit}
      mutators={{ ...arrayMutators }}
      render={(renderProps: FormRenderProps<DocumentValues>) => (
        <form
          className={classNames('sign-up-landing__upload-form', { mobile: isMobile })}
          onSubmit={renderProps.handleSubmit}
        >
          {currentStep < 3 && (
            <LandingDocumentForm
              {...renderProps}
              document={document}
              onDocumentCreate={setDocumentId}
            />
          )}
          {currentStep === 3 && (
            <LandingSetTitleForm
              {...renderProps}
              buttonTitle={
                document?.type
                  ? getFinalStepButtonTitle(document.type)
                  : formattedInitialValues?.type
                  ? getFinalStepButtonTitle(formattedInitialValues.type)
                  : 'Finish'
              }
            />
          )}
        </form>
      )}
    />
  );
};

export default LandingSignDocument;
